import { useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { object, string } from "yup";

import type { LocalAuthorityDetails, OdsOrganisationDetails } from "~/api.js";
import { api, apiHooks } from "~/api.js";
import {
  Button,
  FormLabel,
  HelperText,
  renderErrorToast,
  renderSuccessToast,
  TextField,
} from "~/components/design-system/index.js";

const odsConfigurationSchema = object({
  endpoint: string().min(5).required(),
  apiKey: string().min(5).required(),
});

function OdsConfigurationForm({
  odsConfiguration: { endpoint, apiKey },
  reload,
}: {
  odsConfiguration: { endpoint: string; apiKey: string };
  reload: () => void;
}) {
  return (
    <Formik<{
      endpoint: string;
      apiKey: string;
    }>
      initialValues={{ endpoint, apiKey }}
      validationSchema={odsConfigurationSchema}
      onSubmit={async (values) => {
        const result = await api.configuration.updateOdsConfiguration({
          endpoint: values.endpoint,
          apiKey: values.apiKey,
        });

        if (result.status === 204) {
          renderSuccessToast({ message: "ODS configuration updated" });
          reload();
        } else {
          renderErrorToast({ message: "Failed to update ODS configuration" });
        }
      }}
    >
      {(formikData) => {
        const { values, setValues, errors, touched, submitForm } = formikData;

        return (
          <Box
            sx={{
              maxWidth: "600px",
              marginBottom: "2em",
              gap: "1em",
            }}
          >
            <Box sx={{ mt: "2em" }}>
              <FormLabel
                label={"Endpoint (*)"}
                error={touched.endpoint && !!errors.endpoint}
              />
              <TextField
                name={"endpoint"}
                useFullWidth={true}
                value={values.endpoint}
                onChange={(e) => {
                  setValues({ ...values, endpoint: e });
                }}
              />
              {errors.endpoint && touched.endpoint && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.endpoint as string} />
                </Box>
              )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <FormLabel
                label={"API Key (*)"}
                error={touched.apiKey && !!errors.apiKey}
              />
              <TextField
                name={"apiKey"}
                useFullWidth={true}
                value={values.apiKey}
                onChange={(e) => {
                  setValues({ ...values, apiKey: e });
                }}
              />
              {errors.apiKey && touched.apiKey && (
                <Box sx={{ mb: 4 }}>
                  <HelperText errorMessage={errors.apiKey as string} />
                </Box>
              )}
            </Box>

            <Box sx={{ mt: "0.5em" }}>
              <Button onClick={submitForm} label="Submit" type="submit" />
            </Box>
          </Box>
        );
      }}
    </Formik>
  );
}

type LookupState<T> =
  | { type: "idle" }
  | { type: "loading" }
  | { type: "success"; data: T }
  | { type: "not-found" }
  | { type: "error" };

function OrganisationLookup() {
  const [odsCode, setOdsCode] = useState("");
  const [state, setState] = useState<LookupState<OdsOrganisationDetails>>({
    type: "idle",
  });

  const handleLookup = async () => {
    if (!odsCode.trim()) return;
    setState({ type: "loading" });
    try {
      const response = await api.ods.getOrganisation(odsCode.trim());
      if (response.status === 200) {
        setState({ type: "success", data: response.data });
      } else if (response.status === 404) {
        setState({ type: "not-found" });
      } else {
        setState({ type: "error" });
      }
    } catch {
      setState({ type: "error" });
    }
  };

  return (
    <Box sx={{ maxWidth: "600px" }}>
      <Box sx={{ display: "flex", gap: "1em", alignItems: "center" }}>
        <Box sx={{ flex: 1 }}>
          <FormLabel label="ODS Code" />
          <TextField
            name="organisationOdsCode"
            useFullWidth={true}
            value={odsCode}
            onChange={(e) => {
              setOdsCode(e);
            }}
          />
        </Box>
        <Box sx={{ mt: "1.5em" }}>
          <Button
            onClick={() => {
              void handleLookup();
            }}
            label={state.type === "loading" ? "Looking up..." : "Look up"}
            disabled={state.type === "loading" || !odsCode.trim()}
          />
        </Box>
      </Box>
      {state.type === "success" && (
        <Alert severity="success" sx={{ mt: "1em" }}>
          <strong>{state.data.name}</strong>
          <br />
          {state.data.addressLine1 && (
            <>
              {state.data.addressLine1}
              <br />
            </>
          )}
          {state.data.town && (
            <>
              {state.data.town}
              <br />
            </>
          )}
          {state.data.postCode && (
            <>
              {state.data.postCode}
              <br />
            </>
          )}
          {state.data.telephone && <>Tel: {state.data.telephone}</>}
        </Alert>
      )}
      {state.type === "not-found" && (
        <Alert severity="warning" sx={{ mt: "1em" }}>
          ODS code {odsCode} not found.
        </Alert>
      )}
      {state.type === "error" && (
        <Alert severity="error" sx={{ mt: "1em" }}>
          Failed to look up organisation.
        </Alert>
      )}
    </Box>
  );
}

function LocalAuthorityByOdsLookup() {
  const [odsCode, setOdsCode] = useState("");
  const [state, setState] = useState<LookupState<LocalAuthorityDetails>>({
    type: "idle",
  });

  const handleLookup = async () => {
    if (!odsCode.trim()) return;
    setState({ type: "loading" });
    try {
      const response = await api.ods.getLocalAuthorityByOdsCode(odsCode.trim());
      if (response.status === 200) {
        setState({ type: "success", data: response.data });
      } else if (response.status === 404) {
        setState({ type: "not-found" });
      } else {
        setState({ type: "error" });
      }
    } catch {
      setState({ type: "error" });
    }
  };

  return (
    <Box sx={{ maxWidth: "600px" }}>
      <Box sx={{ display: "flex", gap: "1em", alignItems: "center" }}>
        <Box sx={{ flex: 1 }}>
          <FormLabel label="ODS Code" />
          <TextField
            name="laOdsCode"
            useFullWidth={true}
            value={odsCode}
            onChange={(e) => {
              setOdsCode(e);
            }}
          />
        </Box>
        <Box sx={{ mt: "1.5em" }}>
          <Button
            onClick={() => {
              void handleLookup();
            }}
            label={state.type === "loading" ? "Looking up..." : "Look up"}
            disabled={state.type === "loading" || !odsCode.trim()}
          />
        </Box>
      </Box>
      {state.type === "success" && (
        <Alert severity="success" sx={{ mt: "1em" }}>
          LA code: <strong>{state.data.code}</strong>
          <br />
          Name: {state.data.name ?? "(unknown)"}
        </Alert>
      )}
      {state.type === "not-found" && (
        <Alert severity="warning" sx={{ mt: "1em" }}>
          No local authority found for ODS code {odsCode}.
        </Alert>
      )}
      {state.type === "error" && (
        <Alert severity="error" sx={{ mt: "1em" }}>
          Failed to look up local authority.
        </Alert>
      )}
    </Box>
  );
}

function LocalAuthorityByPostcodeLookup() {
  const [postcode, setPostcode] = useState("");
  const [state, setState] = useState<LookupState<LocalAuthorityDetails>>({
    type: "idle",
  });

  const handleLookup = async () => {
    if (!postcode.trim()) return;
    setState({ type: "loading" });
    try {
      const response = await api.ods.getLocalAuthorityByPostcode(
        postcode.trim(),
      );
      if (response.status === 200) {
        setState({ type: "success", data: response.data });
      } else if (response.status === 404) {
        setState({ type: "not-found" });
      } else {
        setState({ type: "error" });
      }
    } catch {
      setState({ type: "error" });
    }
  };

  return (
    <Box sx={{ maxWidth: "600px" }}>
      <Box sx={{ display: "flex", gap: "1em", alignItems: "center" }}>
        <Box sx={{ flex: 1 }}>
          <FormLabel label="Postcode" />
          <TextField
            name="postcode"
            useFullWidth={true}
            value={postcode}
            onChange={(e) => {
              setPostcode(e);
            }}
          />
        </Box>
        <Box sx={{ mt: "1.5em" }}>
          <Button
            onClick={() => {
              void handleLookup();
            }}
            label={state.type === "loading" ? "Looking up..." : "Look up"}
            disabled={state.type === "loading" || !postcode.trim()}
          />
        </Box>
      </Box>
      {state.type === "success" && (
        <Alert severity="success" sx={{ mt: "1em" }}>
          LA code: <strong>{state.data.code}</strong>
          <br />
          Name: {state.data.name ?? "(unknown)"}
        </Alert>
      )}
      {state.type === "not-found" && (
        <Alert severity="warning" sx={{ mt: "1em" }}>
          No local authority found for postcode {postcode}.
        </Alert>
      )}
      {state.type === "error" && (
        <Alert severity="error" sx={{ mt: "1em" }}>
          Failed to look up local authority.
        </Alert>
      )}
    </Box>
  );
}

export function OdsConfiguration() {
  const [{ data: odsConfiguration, loading: odsConfigurationLoading }, reload] =
    apiHooks.configuration.getOdsConfiguration();

  return (
    <Box sx={{ mt: "2em" }}>
      <Typography variant="h5">ODS Configuration</Typography>
      {odsConfigurationLoading ? (
        <CircularProgress />
      ) : (
        <OdsConfigurationForm
          odsConfiguration={odsConfiguration!}
          reload={reload}
        />
      )}

      <Divider sx={{ my: "2em" }} />

      <Typography variant="h5">Organisation lookup</Typography>
      <Typography variant="body2" sx={{ mt: "0.5em", mb: "1em" }}>
        Look up an organisation&apos;s name and address by ODS code.
      </Typography>
      <OrganisationLookup />

      <Divider sx={{ my: "2em" }} />

      <Typography variant="h5">Local authority by ODS code</Typography>
      <Typography variant="body2" sx={{ mt: "0.5em", mb: "1em" }}>
        Resolve an organisation&apos;s ODS code to its local authority.
      </Typography>
      <LocalAuthorityByOdsLookup />

      <Divider sx={{ my: "2em" }} />

      <Typography variant="h5">Local authority by postcode</Typography>
      <Typography variant="body2" sx={{ mt: "0.5em", mb: "1em" }}>
        Look up the local authority that covers a postcode.
      </Typography>
      <LocalAuthorityByPostcodeLookup />
    </Box>
  );
}
